import React, { type FunctionComponent, useState } from 'react'
import { NoteEdit } from './NoteEdit'
import { API_HOST, authenticatedFetchData } from '../../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { ConfirmDialog } from '../dialogs'
import { hasCharityEditPermission } from '../../../services/permission.service'
import { Button } from '@tomra/design-system-react/button'

type Props = {
  refetchNotes: () => void
  id: string
  created: Date
  name: string
  email: string
  updated: Date
  note: string
}

export const NoteComment: FunctionComponent<Props> = ({ id, created, name, email, updated, note, refetchNotes }) => {
  const [editModeEnabled, setEditMode] = useState<boolean>(false)
  const [deleteStatus, setDeleteStatus] = useState<RequestStatusType>('idle')
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false)

  const deleteNote = () => {
    setDeleteStatus('pending')

    authenticatedFetchData(`${API_HOST}/mytomra/v1.0/hotlinetool/managernotes/${id}`, {
      method: 'DELETE'
    })
      .run()
      .then(
        () => {
          setDeleteStatus('idle')
          refetchNotes()
        },
        (error: Error) => {
          setDeleteStatus('failure')
          logError(new Error('Failed to delete note'), error)
        }
      )
  }

  const date = new Date(updated) > new Date(created) ? updated : created

  return (
    <div className="border-b border-gray-100 pt-4 pb-4">
      {editModeEnabled ? (
        <NoteEdit noteId={id} note={note} setEditMode={setEditMode} refetchNotes={refetchNotes} />
      ) : (
        <div className="flex justify-between">
          <div className="flex-1 mr-4">
            <div className="relative cursor-pointer group">
              <span>{name}</span>
              <span className="absolute py-1 px-2 left-0 bottom-[100%] text-white rounded-lg bg-black opacity-0 transition group-hover:opacity-100">
                {email}
              </span>
            </div>

            <div className="mt-2">{note}</div>

            <div className="relative cursor-pointer group">
              <>{date}</>
              <span className="absolute py-1 px-2 left-0 bottom-[100%] text-white rounded-lg bg-black opacity-0 transition group-hover:opacity-100">
                {new Date(date).toLocaleString('default', { timeZone: 'UTC' }) + ' in UTC'}
              </span>
            </div>
          </div>

          {hasCharityEditPermission() && (
            <>
              <Button variant="icon" className="self-start" aria-label="Edit note" onClick={() => setEditMode(true)}>
                <span className="icon-[fluent--edit-24-regular] size-6" />
              </Button>

              <Button
                variant="icon"
                className="self-start ml-4"
                aria-label="Delete note"
                onClick={() => setConfirmDelete(true)}
              >
                {deleteStatus === 'pending' ? (
                  <div className="loadingSpinner" aria-label="Please wait" />
                ) : (
                  <span className="icon-[fluent--delete-24-regular] size-6" />
                )}
              </Button>
            </>
          )}
        </div>
      )}

      {deleteStatus === 'failure' && (
        <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900 mt-2 mb-4">
          Failed to delete note
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete note?"
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            setConfirmDelete(false)
            deleteNote()
          }}
        />
      )}
    </div>
  )
}
